import * as React from 'react'
import { z } from 'zod'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useApproveMemberOnboarding } from '@workspace/react-query'
import { Button } from '@workspace/ui/components/button'
import { Card, CardContent } from '@workspace/ui/components/card'
import { DatePicker } from '@workspace/ui/components/date-picker'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@workspace/ui/components/form'
import { Input } from '@workspace/ui/components/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@workspace/ui/components/select'
import { Alert, AlertDescription } from '@workspace/ui/components/alert'
import { AlertCircle, Loader2 } from 'lucide-react'
import { authClient } from '@/lib/auth'
import { requireAuth } from '@/lib/middleware'
import { useTopNav } from '@/context/top-nav-provider'
import { Main } from '@/components/layout/main'
import { UpdateUser, updateUserSchema } from '@workspace/utils/types/user'
import type { Resolver } from 'react-hook-form'
import { toast } from 'sonner'
import { useAuthStore } from '@/stores/auth-store'

const searchSchema = z.object({
  draftId: z.string().optional(),
})

export const Route = createFileRoute('/_authenticated/_organization/onboarding/admin')({
  beforeLoad: async () => {
    await requireAuth()
  },
  component: AdminOnboardingPage,
  validateSearch: searchSchema,
})

function AdminOnboardingPage() {
  const navigate = useNavigate()
  const { setLinks, setFixed } = useTopNav()
  const authStore = useAuthStore()
  const user = authStore.auth.user
  const { data: activeMember } = authClient.useActiveMember()
  const { data: activeOrganization } = authClient.useActiveOrganization()
  const organizationId = activeOrganization?.id
  const memberId = activeMember?.id
  const memberRole = activeMember?.role

  const [isSubmitting, setIsSubmitting] = React.useState(false)

  const approveMutation = useApproveMemberOnboarding(
    organizationId || '',
    memberId || ''
  )

  const defaultValues = {
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    phone: user?.phone || '',
    mobile: user?.mobile || '',
    dateOfBirth: user?.dateOfBirth || undefined,
    gender: user?.gender || undefined,
  }

  const form = useForm<UpdateUser>({
    resolver: zodResolver(updateUserSchema) as unknown as Resolver<UpdateUser>,
    defaultValues,
    mode: 'onChange',
  })

  React.useEffect(() => {
    setLinks([
      {
        title: 'Overview',
        href: '/',
      },
      {
        title: 'Onboarding',
        href: '/onboarding/admin',
        isActive: true,
      },
    ])
    setFixed(true)
    return () => {
      setLinks([])
    }
  }, [setLinks, setFixed])

  const handleSuccess = () => {
    navigate({ to: '/' })
  }

  // If member role doesn't match admin roles, redirect
  React.useEffect(() => {
    if (
      memberRole &&
      memberRole !== 'owner' &&
      memberRole !== 'admin' &&
      memberRole !== 'finance' &&
      memberRole !== 'team_leader'
    ) {
      navigate({ to: '/' })
    }
  }, [memberRole, navigate])

  async function onSubmit(data: UpdateUser) {
    if (!organizationId || !memberId) {
      toast.error('Organization or member information is missing')
      return
    }

    try {
      setIsSubmitting(true)

      // First, update user profile
      const { error: updateError } = await authClient.updateUser({
        name: `${data.firstName} ${data.lastName}`,
        title: data.title ?? undefined,
        firstName: data.firstName ?? undefined,
        lastName: data.lastName ?? undefined,
        phone: data.phone ?? undefined,
        mobile: data.mobile ?? undefined,
        dateOfBirth: data.dateOfBirth
          ? new Date(data.dateOfBirth).toISOString()
          : undefined,
      })

      if (updateError) {
        toast.error(updateError.message)
        return
      }

      // Then, approve member onboarding
      await approveMutation.mutateAsync()

      toast.success('Onboarding completed successfully')
      handleSuccess()
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'An error occurred while completing onboarding'
      toast.error(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Main className='flex flex-1 flex-col gap-4 sm:gap-6'>
      <div className='flex flex-wrap items-end justify-between gap-2'>
        <div>
          <h2 className='text-2xl font-bold tracking-tight'>
            Admin Onboarding
          </h2>
          <p className='text-muted-foreground'>
            Complete your profile information to finish onboarding
          </p>
        </div>
      </div>

      <Card>
        <CardContent className='pt-6'>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className='space-y-4'
            >
              <div className='grid grid-cols-2 gap-4'>
                <FormField
                  control={form.control}
                  name='firstName'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>First Name</FormLabel>
                      <FormControl>
                        <Input placeholder='John' {...field} value={field.value ?? ''} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name='lastName'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>Last Name</FormLabel>
                      <FormControl>
                        <Input placeholder='Doe' {...field} value={field.value ?? ''} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className='grid grid-cols-2 gap-4'>
                <FormField
                  control={form.control}
                  name='phone'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone</FormLabel>
                      <FormControl>
                        <Input placeholder='1234567890' {...field} value={field.value ?? ''} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name='mobile'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Mobile</FormLabel>
                      <FormControl>
                        <Input placeholder='1234567890' {...field} value={field.value ?? ''} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className='grid grid-cols-2 gap-4'>
                <FormField
                  control={form.control}
                  name='dateOfBirth'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Date of Birth</FormLabel>
                      <FormControl>
                        <DatePicker
                          value={field.value ?? undefined}
                          onChange={field.onChange}
                          placeholder='Select date of birth'
                          onBlur={field.onBlur}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name='gender'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Gender</FormLabel>
                      <FormControl>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder='Select gender' />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value='male'>Male</SelectItem>
                            <SelectItem value='female'>Female</SelectItem>
                            <SelectItem value='non_binary'>Non-binary</SelectItem>
                            <SelectItem value='other'>Other</SelectItem>
                            <SelectItem value='prefer_not_to_say'>
                              Prefer not to say
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <Alert>
                <AlertCircle className='h-4 w-4' />
                <AlertDescription>
                  Please complete your profile information. Once submitted, your
                  onboarding will be automatically approved.
                </AlertDescription>
              </Alert>

              <Button type='submit' disabled={isSubmitting || approveMutation.isPending}>
                {isSubmitting || approveMutation.isPending ? (
                  <>
                    <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                    Completing...
                  </>
                ) : (
                  'Complete Onboarding'
                )}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
    </Main>
  )
}
